// What shape a phone number has to be in before this app will store it.
//
// One module, read from three places: the entry field in the browser
// (components/PhoneField.jsx), the write path (the normalisers in
// ./people.js, which reach the same rule inside the core), and the Twilio
// transport, which loads this file through the generated `functions/domain`
// mirror and re-exports it. There is one answer to "is this a number we can
// text" (#113), and it is `core/src/phone.rs`, so the phones' copy is the same
// one.
//
// ── Stricter than MyMusicStaff, knowingly ──────────────────────────────────
//
// MMS's phone fields — the student's Mobile Number, and the family contact's
// Mobile, Home and Work — have no country selector, no format hint and no
// validation of any kind: it saves `banana` with SMS Capable ticked and shows
// an `SMS Enabled` badge. Everything below is a knowing divergence.
//
// Our Twilio transport refuses, terminally, any number with no country code —
// see `normalisePhone` below and functions/delivery/transport/twilio.js on why
// a country is never inferred from the studio's locale. Refusing at entry keeps
// such a number out of the database; the country selector and the format hint
// keep the refusal from being a wall (#90). The transport's refusal is the
// backstop for a number that arrives some other way — a script, an import, an
// older document.
//
// MMS also keeps a per-number delivery state — `Delivery Suspended`, with a
// **Resume SMS Delivery** button, keyed to the number — which this app does not
// model (#90): a well-formed but unreachable number shows up only as a failed
// send in the message log.

// `./core.js`, not `../lib/core.js`: this file is copied verbatim into
// functions/domain/, and that door is inside the copy (#218).
import { coreSync } from './core.js';

// ── The rule is `core/src/phone.rs` ─────────────────────────────────────────
//
// A student's number and a contact's three are stored as `storablePhone` makes
// them and refused with `phoneError`'s reason, and the phones get the same
// answers from the same implementation. What is here is the coercion —
// `String(v)`, with a missing value as the empty field — and the sentence out
// of the core's table.
//
// **`E164` is the pattern, not the check.** It is `optionalPhone`'s pattern in
// firestore.rules — the only enforcement a client cannot bypass — and what
// `functions/delivery/transport/twilio.js` re-exports. Nothing decides with
// it: `phone::is_e164` in the core is that pattern written out by hand, and
// `test/domain/phone.test.js` runs the rule's regex, this one and the core
// over the same numbers (#341).
export const E164 = /^\+[1-9]\d{6,14}$/;

// What the core is handed: the value as a string, a missing one as empty. The
// core trims (JavaScript's white space, as `trim()` does) and strips.
const asText = (v) => (v == null ? '' : String(v));

// Formatting removed and nothing else — white space, brackets, dots, slashes,
// the ASCII hyphen, the six Unicode dashes a word processor substitutes for it
// (U+2010–U+2015) and the minus sign (U+2212) a spreadsheet produces. Taking
// them out does not change which phone rings. A missing country code does, so
// it is refused rather than guessed — see the transport for why guessing one
// from the studio's locale has "texted a stranger" as its failure mode.
export function stripPhoneFormatting(raw) {
  return coreSync().stripPhoneFormatting(asText(raw));
}

// The number as an address Twilio can dial, or null.
export function normalisePhone(raw) {
  return coreSync().normalisePhone(asText(raw)) ?? null;
}

// What actually gets written to `phone` on a student or a family.
//
// Stripped rather than stored as typed, where MMS redisplays a number exactly
// as it was entered: what the teacher reads back on the record is the string
// the transport will dial and the string `firestore.rules` matched.
//
// An unparseable value is stripped and kept rather than nulled, so a bad
// number written past the form is refused loudly by `optionalPhone` in
// firestore.rules rather than silently discarded.
export function storablePhone(raw) {
  return coreSync().storablePhone(asText(raw)) ?? null;
}

export function isStorablePhone(raw) {
  return coreSync().isStorablePhone(asText(raw));
}

// The sentence to put under the field, or null when there is nothing wrong.
//
// Empty is not wrong: every phone field is optional, in the rules
// (`optionalPhone`) and on the form, as in MMS.
//
// Three refusals, in order — no `+`, something that is not a digit, not a whole
// international number — and their sentences are the core's table's
// (`people::message`), which the phones read too.
export function phoneError(raw) {
  const refusal = coreSync().phoneError(asText(raw));
  return refusal ? refusal.message : null;
}

// ── Country codes ───────────────────────────────────────────────────────────
//
// Region -> ITU calling code, for exactly the regions ./locale.js can bill in.
//
// Tied to that list: a studio we can invoice is a studio whose families we can
// text. test/domain/phone.test.js asserts the two key sets are identical.
//
// Country *names* are not here: `Intl.DisplayNames` is the CLDR region list,
// localised, as ./locale.js derives the date format from `Intl`.
export const CALLING_CODES = {
  // North America
  US: '1', CA: '1', MX: '52', PR: '1',

  // Euro area
  AT: '43', BE: '32', CY: '357', DE: '49', EE: '372', ES: '34', FI: '358',
  FR: '33', GR: '30', HR: '385', IE: '353', IT: '39', LT: '370', LU: '352',
  LV: '371', MT: '356', NL: '31', PT: '351', SI: '386', SK: '421',

  // Rest of Europe
  GB: '44', CH: '41', NO: '47', SE: '46', DK: '45', IS: '354',
  PL: '48', CZ: '420', HU: '36', RO: '40', BG: '359', RS: '381',
  UA: '380', TR: '90', RU: '7',

  // Asia-Pacific
  AU: '61', NZ: '64', JP: '81', KR: '82', CN: '86', TW: '886',
  HK: '852', SG: '65', MY: '60', TH: '66', ID: '62', PH: '63',
  VN: '84', IN: '91', PK: '92', BD: '880', LK: '94',

  // Middle East and Africa
  AE: '971', SA: '966', QA: '974', KW: '965', BH: '973', OM: '968',
  JO: '962', IL: '972', EG: '20', ZA: '27', NG: '234', KE: '254',
  GH: '233', MA: '212', TN: '216', DZ: '213',

  // Latin America
  BR: '55', AR: '54', CL: '56', CO: '57', PE: '51', UY: '598',
  CR: '506', DO: '1', GT: '502', PA: '507', EC: '593',
};

// One calling code can belong to several regions — +1 is the whole North
// American Numbering Plan, and US, CA, PR and DO all sit on it here. Nothing is
// stored about which one a number belongs to: the address is the code and the
// digits. Which row the dropdown highlights for a number that already has a
// code is stated rather than left to key order — +1 is US and +7 is RU — by
// `phone::region_for_phone`.
//
// **The three functions below are `core/src/phone.rs`'s**, so the phones'
// pickers highlight, prefix and hint exactly as this one does. The table above
// stays a literal because the dropdown reads it at module scope;
// `test/domain/peopleCore.test.js` holds it to the core's.
//
// The core's table has no prototype, so `phoneHint('toString')` finds no
// country code, where `CALLING_CODES[region]` would read `Object.prototype`.

// The region whose row the selector should show for a number already in the
// box, or null if it has no recognisable code yet. Longest match wins, so a two-
// or three-digit code is preferred over any shorter one it happens to begin
// with: +385 is Croatia, not +3 followed by 85.
export function regionForPhone(raw) {
  return coreSync().regionForPhone(asText(raw)) ?? null;
}

// Put `region`'s calling code on the front of whatever is in the box, replacing
// a code that is already there rather than stacking a second one on it.
//
// So the selector cannot disagree with the field: it does not hold a country,
// it edits the string. A British number pasted into a box showing "United
// States" stays British, and the dropdown follows the text. A stored country
// combined with a typed national part could text a stranger.
export function withCallingCode(raw, region) {
  return coreSync().withCallingCode(asText(raw), asText(region));
}

// What to tell a teacher looking at an empty box, or at a country they have
// just chosen. Not a per-country example number, which could dial somebody.
export function phoneHint(region) {
  return coreSync().phoneHint(typeof region === 'string' ? region : null);
}
